import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { uploadData, getUrl, remove } from 'aws-amplify/storage'
import { toast } from '../../Toast'
import { TrashCanFull, UploadCloudFull } from '../../../../icons'
import './image-uploader.scss'

const ImageUploader = ({ templateId, sectionId, fileSrc, fileKey, handleImage }) => {
  const { t } = useTranslation()
  const [imageSource, setImageSource] = useState(fileSrc)
  const [imageKey, setImageKey] = useState(fileKey)
  const [showDeleteButton, setShowDeleteButton] = useState(false)
  const fileInput = useRef(null)
  const acceptedFileTypes = ['image/svg', 'image/svg+xml', 'image/png', 'image/jpg', 'image/jpeg']
  const maxFileSize = 5000000
  const s3ColteneImagesFolder = 'coltene-landing/images'

  useEffect(() => {
    const loadS3Image = async (key) => {
      const { url } = await getUrl({
        key,
        options: {
          accessLevel: 'public',
        },
      })
      setImageSource(url.toString())
    }
    if (imageKey?.length > 0 && !imageSource) {
      loadS3Image(imageKey).catch((err) => console.log('getUrl failed', err))
      return
    }
    setImageSource(fileSrc)
  }, [fileSrc])

  useEffect(() => {
    setImageKey(fileKey)
  }, [fileKey])

  const handleAdd = () => {
    if (imageSource) return
    fileInput.current?.click()
  }

  const handleRemove = async () => {
    if (imageKey && !imageKey.includes('default')) {
      try {
        await remove({
          key: imageKey,
          options: { accessLevel: 'public' },
        })
      } catch (error) {
        console.log('Remove image failed', error)
      }
    }
    handleImage('', null)
    setImageSource('')
    setImageKey(null)
  }

  const uploadSingleFile = async (e) => {
    const file = e.target.files?.[0]

    if (!file) return

    if (!acceptedFileTypes.includes(file.type)) {
      toast.error(t('error.file_type_not_supported'))
      return
    }

    if (file.size > maxFileSize) {
      toast.error(t('error.file_too_lager'))
      return
    }

    try {
      const ext = file.name.split('.').pop()
      const storagePath = `${s3ColteneImagesFolder}/${templateId}-${sectionId}.${ext}`
      const isSVG = ext === 'svg'
      await uploadData({
        key: storagePath,
        data: file,
        options: {
          accessLevel: 'public',
          contentType: isSVG ? 'image/svg+xml' : file.type,
        },
      }).result

      const { url } = await getUrl({
        key: storagePath,
        options: { accessLevel: 'public' },
      })

      const imgUrl = url.toString()

      handleImage(imgUrl, storagePath)
      setImageSource(imgUrl)
      setImageKey(storagePath)
    } catch (error) {
      console.log('error', error)
      toast.error('An error occurred while updating the image')
    } finally {
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  return (
    <div className="image-uploader" onMouseEnter={() => setShowDeleteButton(true)} onMouseLeave={() => setShowDeleteButton(false)} onClick={handleAdd}>
      <input
        ref={fileInput}
        type="file"
        accept="image/svg, image/png, image/jpg, image/jpeg, image/svg+xml"
        style={{ display: 'none' }}
        onChange={uploadSingleFile}
      />
      {imageSource && <img className={'image' + (showDeleteButton ? ' with-transparency' : '')} src={imageSource} alt="background" />}
      {!imageSource && (
        <div className="add-image-wrapper">
          <UploadCloudFull className="upload-svg" />
          {t('upload_image')}
        </div>
      )}
      {imageSource && showDeleteButton && (
        <div
          className="remove-image"
          onClick={(ev) => {
            ev.stopPropagation()
            handleRemove()
          }}
        >
          <TrashCanFull />
        </div>
      )}
    </div>
  )
}

export default ImageUploader

